import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceService } from '../../service.service';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import Swal from 'sweetalert2';

interface Question {
  question_id: string;
  question_text: string;
  question_type: 'star_rating' | 'text' | 'multiple_choice';
  options?: string[];
  is_required: boolean;
  _id?: string;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
  _id?: string;
}

interface SurveyDetail {
  survey_id: string;
  survey_title: string;
  survey_description: string;
  customer_id: string;
  customer_name: string;
  questionnaire_sets: QuestionnaireSet[];
}

@Component({
  selector: 'app-feedback-update',
  templateUrl: './feedback-update.component.html',
  styleUrls: ['./feedback-update.component.css']
})
export class FeedbackUpdateComponent implements OnInit {
  feedbackForm!: FormGroup;
  surveyDetail: SurveyDetail | null = null;
  
  surveyId: string = '';
  customerId: string = '';
  employeeId: string = '';
  
  isLoading: boolean = false;
  isSubmitting: boolean = false;

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ServiceService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    // Get parameters from route
    this.route.params.subscribe(params => {
      this.surveyId = params['survey_id'];
    });

    this.route.queryParams.subscribe(params => {
      this.customerId = params['customer_id'];
      this.employeeId = params['employee_id'] || localStorage.getItem('admin_id') || '';
    });

    if (this.surveyId && this.customerId && this.employeeId) {
      this.loadSurveyDetails();
    } else {
      this.Toast.fire({
        icon: 'error',
        title: 'Missing required parameters'
      });
      this.goBack();
    }
  }

  loadSurveyDetails(): void {
    this.isLoading = true;
    
    this.service.getCustomerSurveyDetail(this.surveyId).subscribe({
      next: (response) => {
        if (response.success) {
          this.surveyDetail = response.data;
          this.initializeForm();
        } else {
          this.Toast.fire({
            icon: 'error',
            title: response.message || 'Failed to load survey details'
          });
          this.goBack();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading survey:', error);
        this.Toast.fire({
          icon: 'error',
          title: 'Failed to load survey details'
        });
        this.isLoading = false;
        this.goBack();
      }
    });
  }

  initializeForm(): void {
    this.feedbackForm = this.fb.group({
      answers: this.fb.array([]),
      remarks: ['']
    });

    // Create form controls for all questions
    this.surveyDetail?.questionnaire_sets.forEach(set => {
      set.questions.forEach(question => {
        this.addAnswerControl(set.set_id, question);
      });
    });
  }

  get answersArray(): FormArray {
    return this.feedbackForm.get('answers') as FormArray;
  }

  addAnswerControl(setId: string, question: Question): void {
    const answerGroup = this.fb.group({
      question_id: [question.question_id, Validators.required],
      set_id: [setId, Validators.required],
      star_rating: [question.question_type === 'star_rating' ? null : undefined],
      text_answer: [question.question_type === 'text' ? '' : undefined],
      selected_option: [question.question_type === 'multiple_choice' ? '' : undefined]
    });

    // Add required validator if question is required
    if (question.is_required) {
      if (question.question_type === 'star_rating') {
        answerGroup.get('star_rating')?.setValidators(Validators.required);
      } else if (question.question_type === 'text') {
        answerGroup.get('text_answer')?.setValidators(Validators.required);
      } else if (question.question_type === 'multiple_choice') {
        answerGroup.get('selected_option')?.setValidators(Validators.required);
      }
    }

    this.answersArray.push(answerGroup);
  }

  getAnswerControl(setId: string, questionId: string): FormGroup | null {
    const control = this.answersArray.controls.find(ctrl => 
      ctrl.get('set_id')?.value === setId && 
      ctrl.get('question_id')?.value === questionId
    );
    return control as FormGroup || null;
  }

  // Helper method to get text answer control with proper typing
  getTextAnswerControl(setId: string, questionId: string): FormControl {
    const control = this.getAnswerControl(setId, questionId);
    return control?.get('text_answer') as FormControl;
  }

  // Helper method to get selected option control with proper typing
  getSelectedOptionControl(setId: string, questionId: string): FormControl {
    const control = this.getAnswerControl(setId, questionId);
    return control?.get('selected_option') as FormControl;
  }

  setStarRating(setId: string, questionId: string, rating: number): void {
    const control = this.getAnswerControl(setId, questionId);
    if (control) {
      control.get('star_rating')?.setValue(rating);
      control.get('star_rating')?.markAsTouched();
    }
  }

  getStarRating(setId: string, questionId: string): number {
    const control = this.getAnswerControl(setId, questionId);
    return control?.get('star_rating')?.value || 0;
  }

  submitFeedback(): void {
  if (this.feedbackForm.invalid) {
    this.markFormGroupTouched(this.feedbackForm);
    this.Toast.fire({
      icon: 'warning',
      title: 'Please answer all required questions'
    });
    return;
  }

  // Navigate away instantly
  this.goBack();
  this.Toast.fire({
    icon: 'success',
    title: 'Feedback submitted successfully!'
  });

  // Submit in background
  const formValue = this.feedbackForm.value;
  const answers = formValue.answers
    .map((answer: any) => {
      const result: any = {
        question_id: answer.question_id,
        set_id: answer.set_id
      };
      if (answer.star_rating !== null && answer.star_rating !== undefined) {
        result.star_rating = answer.star_rating;
      }
      if (answer.text_answer !== null && answer.text_answer !== undefined && answer.text_answer !== '') {
        result.text_answer = answer.text_answer;
      }
      if (answer.selected_option !== null && answer.selected_option !== undefined && answer.selected_option !== '') {
        result.selected_option = answer.selected_option;
      }
      return result;
    })
    .filter((answer: any) =>
      answer.star_rating !== undefined ||
      answer.text_answer !== undefined ||
      answer.selected_option !== undefined
    );

  const feedbackData = {
    survey_id: this.surveyId,
    employee_id: this.employeeId,
    customer_id: this.customerId,
    answers: answers,
    remarks: formValue.remarks || ''
  };

  this.service.addCustomerSurveyFeedback(this.surveyId, feedbackData).subscribe({
    next: (response) => {
      if (!response.success) {
        this.Toast.fire({
          icon: 'error',
          title: response.message || 'Failed to submit feedback'
        });
      }
    },
    error: (error) => {
      console.error('Error submitting feedback:', error);
      this.Toast.fire({
        icon: 'error',
        title: 'Failed to submit feedback'
      });
    }
  });
}

  goBack(): void {
    this.router.navigate(['/admin/customer-survey-menu/feedbackintake']);
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }
}